import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchPage = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [products, setProducts] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (query.trim()) {
      const searchLower = query.toLowerCase();
      const filtered = products.filter((product) => {
        const nameHu = product.name?.hu?.toLowerCase() || '';
        const nameEn = product.name?.en?.toLowerCase() || '';
        const descHu = product.description?.hu?.toLowerCase() || '';
        const descEn = product.description?.en?.toLowerCase() || '';
        const brand = product.brand?.toLowerCase() || '';
        const category = product.category?.toLowerCase() || '';
        
        return (
          nameHu.includes(searchLower) ||
          nameEn.includes(searchLower) ||
          descHu.includes(searchLower) ||
          descEn.includes(searchLower) ||
          brand.includes(searchLower) ||
          category.includes(searchLower)
        );
      });
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query, products]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-4">
            {language === 'hu' ? 'Keresés' : 'Search'}
          </h1>
          
          <form onSubmit={handleSearch} className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={language === 'hu' ? 'Keresés termékek között...' : 'Search products...'}
              className="pl-12 pr-4 py-3 h-12 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 text-base"
              data-testid="search-input"
              autoFocus
            />
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-500">{t.common.loading}</p>
          </div>
        ) : query.trim() ? (
          <>
            <p className="text-zinc-400 mb-6">
              {results.length} {language === 'hu' ? 'találat' : 'results'} "{query}"
            </p>
            {results.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {results.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Search className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400 text-lg">
                  {language === 'hu' ? 'Nincs találat' : 'No results found'}
                </p>
                <p className="text-zinc-500 mt-2">
                  {language === 'hu' 
                    ? 'Próbálj más kulcsszavakat' 
                    : 'Try different keywords'}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <Search className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">
              {language === 'hu' 
                ? 'Írj be kulcsszót a kereséshez' 
                : 'Enter a keyword to search'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
